import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

// The three axes a color's spread is drawn on, in the order they are shown.
export type SpreadAxis = {
  key: "L" | "C" | "H";
  label: string;
  format: (value: number) => string;
};

export const SPREAD_AXES: SpreadAxis[] = [
  { key: "L", label: "Lightness", format: (value) => value.toFixed(3) },
  { key: "C", label: "Chroma", format: (value) => value.toFixed(4) },
  {
    key: "H",
    label: "Hue",
    format: (value) => `${(((value % 360) + 360) % 360).toFixed(1)}°`,
  },
];

// A hue turned by whole turns to lie within half a turn of the color's median hue, on the same turn
// as its stored percentiles, so a color across 0° reads as one interval. Turning from the median and
// not from the lowest member's hue matters for the stones that are not members: one a little below
// that lowest hue belongs just left of the box, not a whole turn away on its right.
export const unwrappedHue = (hue: number, median: number) =>
  median + ((((hue - median + 180) % 360) + 360) % 360) - 180;

type SpreadBoxProps = {
  label: string;
  // The 0th, 5th, 50th, 95th and 100th percentile: the whiskers, the box and the median.
  percentiles: number[];
  // Stones to show, one gray tick each — they may fall outside the whiskers.
  values?: number[];
  // One stone to pick out, as a red line — it may fall outside the whiskers too.
  marker?: number | null;
  // An interval drawn around the box as an outlined rectangle, e.g. where the color's range
  // probably lies.
  outline?: number[] | null;
  // How a value is written under the axis, e.g. a hue taken back into 0..360.
  format: (value: number) => string;
};

const WIDTH = 300;
const HEIGHT = 64;
const MARGIN = 12;
const BOX_TOP = 18;
const BOX_HEIGHT = 20;

// One axis of a color's spread, drawn horizontally: the whiskers span P0–P100, the box P5–P95 with
// the median marked, each stone is a tick on the box's line, the stone picked out a red line, and
// an interval around it a dashed rectangle.
// The scale is the data's own, with no forced zero, so a narrow color is drawn as wide as a broad
// one.
const SpreadBox = ({
  label,
  percentiles,
  values = [],
  marker,
  outline,
  format,
}: SpreadBoxProps) => {
  const theme = useTheme();
  const [lowest, low, median, high, highest] = percentiles;

  const all = [
    ...percentiles,
    ...values,
    ...(marker != null ? [marker] : []),
    ...(outline ?? []),
  ];
  const start = Math.min(...all);
  const end = Math.max(...all);
  const span = end - start || 1;
  const x = (value: number) =>
    MARGIN + ((value - start) / span) * (WIDTH - 2 * MARGIN);
  const middle = BOX_TOP + BOX_HEIGHT / 2;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {/* Drawn at its own size in pixels, never stretched: its lines and text keep their size
          and follow the page's zoom like the text around it. The page lays the plots out. */}
      <svg width={WIDTH} height={HEIGHT} style={{ display: "block" }}>
        {values.map((value, index) => (
          <line
            key={index}
            x1={x(value)}
            x2={x(value)}
            y1={BOX_TOP - 4}
            y2={BOX_TOP + BOX_HEIGHT + 4}
            stroke={theme.palette.text.secondary}
            strokeOpacity={0.5}
          />
        ))}
        <line
          x1={x(lowest)}
          x2={x(highest)}
          y1={middle}
          y2={middle}
          stroke={theme.palette.primary.main}
        />
        {[lowest, highest].map((value) => (
          <line
            key={value}
            x1={x(value)}
            x2={x(value)}
            y1={BOX_TOP + 4}
            y2={BOX_TOP + BOX_HEIGHT - 4}
            stroke={theme.palette.primary.main}
          />
        ))}
        <rect
          x={x(low)}
          y={BOX_TOP}
          width={x(high) - x(low)}
          height={BOX_HEIGHT}
          fill={theme.palette.primary.main}
          fillOpacity={0.15}
          stroke={theme.palette.primary.main}
        />
        <line
          x1={x(median)}
          x2={x(median)}
          y1={BOX_TOP}
          y2={BOX_TOP + BOX_HEIGHT}
          stroke={theme.palette.primary.main}
          strokeWidth={2}
        />
        {outline && (
          <rect
            x={x(outline[0])}
            y={BOX_TOP - 8}
            width={x(outline[1]) - x(outline[0])}
            height={BOX_HEIGHT + 16}
            fill="none"
            stroke={theme.palette.primary.main}
            strokeDasharray="4 3"
          />
        )}
        {marker != null && (
          <line
            x1={x(marker)}
            x2={x(marker)}
            y1={BOX_TOP - 6}
            y2={BOX_TOP + BOX_HEIGHT + 6}
            stroke={theme.palette.error.main}
            strokeWidth={2}
          />
        )}
        {[start, median, end].map((value, index) => (
          <text
            key={index}
            x={x(value)}
            y={HEIGHT - 4}
            fontSize={10}
            textAnchor={index === 0 ? "start" : index === 2 ? "end" : "middle"}
            fill={theme.palette.text.secondary}
          >
            {format(value)}
          </text>
        ))}
      </svg>
    </Box>
  );
};

export default SpreadBox;
